/**
 * Wizard config — data-driven step definitions shared by web + mobile.
 *
 * Phase 20 (Refactor 3). Sits alongside `../wizard-schemas/` (Refactor 2,
 * the Zod schemas) and `@thesitebook/strings` (Refactor 4, the copy
 * table). The split is intentional:
 *
 *   - `wizard-schemas`  — WHAT data the wizard collects (validation)
 *   - `wizard-config`   — HOW it's collected (which step renders which
 *                         field, in what order, with what label key)
 *   - `@thesitebook/strings` — the user-facing copy itself
 *
 * Each renderer (mobile today, web later) iterates `WIZARD_CONFIGS[kind]`
 * and renders the step body either by:
 *
 *   - data-driven path: iterate `step.fields[]` and call a registered
 *     primitive for each `WizardFieldType` (text → TextInput, etc.); OR
 *   - custom renderer: look up `step.customRenderer` in a renderer map
 *     supplied by the screen (e.g. `{ HazardBuilder, ControlsBuilder }`).
 *
 * Steps with bespoke UIs (multi-select pickers, hazard severity rows,
 * worker pickers) live as custom renderers because they can't be
 * expressed as a flat list of typed fields without inventing a much
 * larger schema. That trade-off is documented per step and revisited
 * when v3.1 introduces native primitives (date picker, photo attach).
 *
 * AI section-fill (Phase 13) is declared as `aiSectionSuggest` on the
 * step. The renderer wires the button automatically when present, so
 * screens no longer have to hand-thread `apiBundle.ai` into bespoke step
 * components just to add a "Suggest with AI" affordance.
 */
import type { WizardKind } from "../wizard-schemas/index";

/**
 * Field primitives the generic renderer knows how to draw. Anything more
 * exotic (multi-select with severity, attendee picker) must use
 * `customRenderer` rather than extending this enum — the goal is to keep
 * the generic path small and predictable.
 *
 * The `date` variant currently renders as a labelled `TextInput`
 * accepting ISO `YYYY-MM-DD` until a native date-picker primitive lands
 * in v3.1 — see Phase 13 RAMS screen `datePickerHelper` for the existing
 * pattern. The renderer surfaces `helperKey` so callers can communicate
 * the format expectation.
 */
export type WizardFieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "single-select"
  | "multi-select"
  | "checklist"
  | "toggle"
  | "rich-text"
  | "custom";

export interface WizardFieldOption {
  /** Value persisted in the wizard data — usually a stable id. */
  readonly value: string;
  /** Strings table key for the user-facing label. */
  readonly labelKey: string;
}

export interface WizardFieldDefinition {
  /** Matches the corresponding field name in the step's Zod schema. */
  readonly id: string;
  /** Primitive the renderer will draw. */
  readonly type: WizardFieldType;
  /** Strings table key for the field label (e.g. `wizards.rams.fields.jobDescription`). */
  readonly labelKey: string;
  /** Optional placeholder copy. */
  readonly placeholderKey?: string;
  /** Optional helper / hint copy shown below the field. */
  readonly helperKey?: string;
  /** Whether the field is required (renderer renders the asterisk). */
  readonly required?: boolean;
  /** For `single-select` / `multi-select` / `checklist` — the options. */
  readonly options?: ReadonlyArray<WizardFieldOption>;
  /** For `text` — force multiline behaviour. */
  readonly multiline?: boolean;
  /** For `textarea` — number of rows hint. */
  readonly rows?: number;
  /**
   * For `text` / `textarea` — when true, the renderer shows a small
   * "Rewrite in plain English" affordance below the field that calls
   * `POST /api/mobile/v1/ai/rewrite-plain` with the current value and
   * lets the user replace it with a worker-friendly version.
   *
   * Opt-in per field (not all narrative fields benefit; reserve for the
   * 1-2 longest plain-English fields per wizard).
   */
  readonly aiRewritePlain?: boolean;
}

/**
 * AI section-suggest hook declared once per step. The renderer reads
 * this and renders the button under the matching field; no per-screen
 * wiring required.
 *
 * `documentType` is typed against `AISectionDocType` indirectly — it's a
 * plain string here to avoid a circular import with `entities/ai`.
 * Callers should pass one of the values from `AI_SECTION_DOC_TYPES`.
 */
export interface WizardAISectionSuggestConfig {
  readonly enabled: boolean;
  /** Document discriminator the dispatcher routes on (e.g. "RAMS"). */
  readonly documentType: string;
  /** Section identifier the server uses to build the prompt. */
  readonly sectionKey: string;
  /** Which field in the step receives the suggestion. */
  readonly targetFieldId: string;
  /** When true, the renderer disables the button (e.g. RAMS pre-Phase-14b). */
  readonly disabled?: boolean;
  /** Optional strings key explaining the disabled state. */
  readonly disabledReasonKey?: string;
}

/**
 * Predicate type for conditional steps. Receives the partial wizard
 * data and returns true to hide the step.
 *
 * The predicate runs against the current draft, so step visibility can
 * react to earlier-step answers (e.g. RAMS hides the "controls" step
 * when no hazards are picked).
 */
export type WizardStepIsHidden = (data: Record<string, unknown>) => boolean;

export interface WizardStepConfig {
  /** Matches a step id in `WIZARD_SCHEMAS[<kind>].steps`. */
  readonly id: string;
  /** Strings table key for the step heading. */
  readonly titleKey: string;
  /** Optional one-line description shown under the title. */
  readonly descriptionKey?: string;
  /**
   * Dot-path into the wizard's schema bundle for this step's Zod schema,
   * e.g. `"RAMS.steps.context"`. The renderer uses this only for
   * debugging / docs — runtime validation is wired by the existing
   * `validationSchemas` map on the screen.
   */
  readonly schemaRef: string;
  /**
   * When present, the step is data-driven and the renderer iterates
   * over `fields` to draw each one via its `WizardFieldType` primitive.
   *
   * MUTUALLY EXCLUSIVE with `customRenderer`. Steps that need bespoke
   * UI (multi-select pickers, severity chips, worker pickers, photo
   * attach) MUST use `customRenderer`.
   */
  readonly fields?: ReadonlyArray<WizardFieldDefinition>;
  /**
   * When present, the renderer looks this string up in the
   * `customRenderers` prop and renders the resulting component. The
   * component receives the step config + the form context.
   *
   * MUTUALLY EXCLUSIVE with `fields`.
   */
  readonly customRenderer?: string;
  /** AI section-suggest hook (optional). */
  readonly aiSectionSuggest?: WizardAISectionSuggestConfig;
  /** Conditional visibility predicate. Defaults to "always shown". */
  readonly isHidden?: WizardStepIsHidden;
}

export interface WizardConfig {
  /** Matches `WIZARD_KINDS` in `../wizard-schemas/index.ts`. */
  readonly documentType: WizardKind;
  /**
   * Optional variant tag — purely informational at the config layer;
   * mobile uses `documentType` for routing. `CPP` ships as `FULL` and
   * `LIGHTWEIGHT` (the latter persists as DocumentType.CPP in Prisma —
   * see CLAUDE.md memory `project_lightweight_cpp`).
   */
  readonly variant?: "FULL" | "LIGHTWEIGHT";
  /** Steps in display order — includes the no-validation preview step. */
  readonly steps: ReadonlyArray<WizardStepConfig>;
  /** Strings key for the submit button. Defaults to `common.actions.submit`. */
  readonly submitButtonKey?: string;
}
